import React from 'react';
import { useDispatch } from 'react-redux';
import { ThreeDotsDropdown } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import { showErrorThunk, showSuccessThunk } from '@spike/notifications-action';
import { faEye, faPaperPlane, faTrash } from '@fortawesome/pro-light-svg-icons';
import { ClientFormRecord } from './ClientFormsTable';
import { resendFormInvitationThunk } from '@spike/forms-action';
import useApiClientWrapper from 'hooks/useApiClientWrapper';

interface Props {
    status: 'pending' | 'completed';
    record: ClientFormRecord;
    onView?: (record: ClientFormRecord) => void;
    lastRecord?: boolean;
}

const ClientFormOptions: React.FC<Props> = ({ status, record, onView, lastRecord }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper() as any;
    const [selected, setSelected] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    // Inline view removed – we'll navigate to a page-level view handled by the parent

    // MVP: only show Resend on Pending; hide Complete/Delete
    const optionsPending: ThreeDotsDropdownOption[] = [
        { id: 'resend', label: 'Resend', icon: faPaperPlane, onClick: () => setSelected('resend') }
    ];

    // MVP: hide Delete option; only View is available for completed forms
    const optionsCompleted: ThreeDotsDropdownOption[] = [
        { id: 'view', label: 'View', icon: faEye, onClick: () => setSelected('view') }
    ];

    React.useEffect(() => {
        if (!selected) return;
        const run = async () => {
            try {
                switch (selected) {
                    case 'resend': {
                        if (loading) return;
                        setLoading(true);
                        const invitationId =
                            (record as any).invitationId || (record as any).invitation_id || (record as any).id;
                        if (!invitationId) throw new Error('Missing invitation id');
                        const result = await (dispatch as any)(
                            (resendFormInvitationThunk as any)(apiClientWrapper, String(invitationId))
                        );
                        const msg = result?.message || 'Form resent successfully';
                        dispatch(showSuccessThunk(msg));
                        break;
                    }
                    case 'view':
                        onView?.(record);
                        break;
                    case 'delete':
                        // Hidden in MVP; fallback safety
                        dispatch(showErrorThunk('Delete is disabled in MVP.'));
                        break;
                    case 'complete-now':
                        dispatch(showErrorThunk('Complete Now is not implemented yet.'));
                        break;
                    default:
                        break;
                }
            } catch (e: any) {
                const msg = e?.message || 'Failed to resend form';
                dispatch(showErrorThunk(msg));
            } finally {
                setLoading(false);
            }
        };
        void run();
        setSelected(null);
    }, [selected, dispatch, apiClientWrapper, record, loading]);

    const options = status === 'completed' ? optionsCompleted : optionsPending;
    return (
        <ThreeDotsDropdown
            options={options}
            direction={lastRecord ? 'up' : 'down'}
        />
    );
};

export default ClientFormOptions;
